import { Icon } from '@design/components/icon/Icon';
import clsx from 'clsx';
import { Property } from 'csstype';
import { forwardRef, ReactNode, TableHTMLAttributes, useState } from 'react';

import styles from './TableHeader.module.scss';

export enum SortDirection {
  asc,
  dsc,
}

export interface TableHeaderProps
  extends TableHTMLAttributes<HTMLTableCellElement> {
  alignment?: Property.TextAlign;
  colSpan?: HTMLTableCellElement['colSpan'];
  rowSpan?: HTMLTableCellElement['rowSpan'];
  isSortable?: boolean;
  isSorted?: boolean;
  isHoverEnabled?: boolean;
  fixed?: boolean;
  headerClick?: (sortDirection: SortDirection) => void;
  children?: ReactNode;
  isOver?: boolean;
  dropPosition?: string;
}

const getLabelJustifyContent = (alignment: Property.TextAlign) => {
  switch (alignment) {
    case 'left':
      return 'start';
    case 'right':
      return 'end';
    default:
      return alignment;
  }
};

export const TableHeader = forwardRef<HTMLTableCellElement, TableHeaderProps>(
  (
    {
      alignment = 'end',
      isSortable,
      isSorted,
      isHoverEnabled = true,
      fixed,
      headerClick,
      children,
      isOver = false,
      dropPosition,
      className,
      ...rest
    },
    ref
  ) => {
    const [sortDirection, setSortDirection] = useState(SortDirection.dsc);

    const handleClick = () => {
      if (!isSortable) {
        return;
      }

      const changeInDirection =
        sortDirection === SortDirection.dsc
          ? SortDirection.asc
          : SortDirection.dsc;
      headerClick?.(changeInDirection);
      setSortDirection(changeInDirection);
    };

    const barSortDirection =
      sortDirection === SortDirection.dsc ? 'BarsSortDown' : 'BarsSortUp';

    return (
      <th
        data-testid="table-header"
        data-table-header
        onClick={headerClick && handleClick}
        className={clsx(
          styles.tableHeader,
          isSortable && styles.isSortable,
          fixed && styles.fixed,
          isHoverEnabled && styles.isHoverEnabled,
          isOver && dropPosition === 'left' && styles.isLeftOver,
          isOver && dropPosition === 'right' && styles.isRightOver,
          className
        )}
        style={{ textAlign: alignment }}
        ref={ref}
        {...rest}
      >
        <span
          className={styles.label}
          style={{ justifyContent: getLabelJustifyContent(alignment) }}
        >
          {children}
          {isSortable && (
            <Icon
              className={clsx(styles.filterIcon, isSorted && styles.isSorted)}
              size={12}
              iconName={isSorted ? barSortDirection : 'Sort'}
            />
          )}
        </span>
      </th>
    );
  }
);
